import React, { useEffect, useState, useCallback, useRef } from 'react'
import DataTable from './DataTable'
import { ConfirmModal } from './ConfirmModal';
import { EditModal } from './EditModal';
import { AIGenerateModal } from './AIGenerateModal';
import { Article, Story, NewStory } from '../types';
import ImagePicker from './ImagePicker';
import Modal from './Modal';
import { createStory, deleteStory, updateStory } from '../api/storyApi';
import { uploadImage } from '../api/imageApi';
import Toast from './Toast';
import icon_ai from '../assets/actions/icon_ai.png';
import './Articles.css';
import { generateImage } from '../api/ai';
import { createStoryImageWithText, createImageWithTextPreview, UserSettings } from '../utils/imageTextOverlay';
import { getUserSettings } from '../api/userSettingsApi';
import { useAuth } from '../context/AuthContext';

// Helper function to convert data URL to File object
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const Stories = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({});
  const [reload, setReload] = useState(false);
  const [showModalConfirmDelete, setShowModalConfirmDelete] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [showAIImageConfirmModal, setShowAIImageConfirmModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [creationType, setCreationType] = useState('edit');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showAIModal, setShowAIModal] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previousImage, setPreviousImage] = useState<string | null>(null);
  const [baseImageUrl, setBaseImageUrl] = useState<string | null>(null);
  const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);

  const [article, setArticle] = useState<Article | Story | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: ''
  });

  const columnDefinitions = [
    { id: 'title', label: 'Título', align: 'left' },
    { id: 'name', label: 'Creado por', align: 'center' },
    { id: 'created_at', label: 'Fecha', align: 'center' },
    {
      id: 'planned', label: 'Planificado', align: 'center',
      accessorFn: (row: any) => {
        return row.planned && Array.isArray(row.planned)
          ? row.planned.map((item: string, index: number) => (
            <React.Fragment key={index}>
              {item !== 'null' ? item : '-'}
              <br />
            </React.Fragment>
          ))
          : 'No';
      },
    },
    {
      id: 'published', label: 'Publicado', align: 'center',
      accessorFn: (row: any) => {
        return row.published && Array.isArray(row.planned)
          ? row.published.map((item: string, index: number) => (
            <React.Fragment key={index}>
              {item !== 'null' ? item : '-'}
              <br />
            </React.Fragment>
          ))
          : 'No';
      },
    },
    {
      id: 'actions',
      type: 'actions',
      label: '',
      align: 'center',
      actions: [
        {
          type: 'edit',
          onClick: (row: any) => {
            setArticle(row);
            setCreationType('edit');
            setShowModalEdit(true);
          },
        },
        {
          type: 'delete',
          onClick: (row: any) => {
            setArticle(row);
            setShowModalConfirmDelete(true);
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (article?.id) {
      // For existing articles, check if we have both base and processed images
      const imageUrl = article.image_url || null;
      const baseUrl = article.base_image_url || null;
      
      setSelectedImage(imageUrl);
      setProcessedImageUrl(imageUrl);
      setBaseImageUrl(baseUrl);
      
      // If we don't have a base image but have a processed image,
      // we'll treat the processed image as the base for editing
      if (!baseUrl && imageUrl) {
        setBaseImageUrl(imageUrl);
      }
    } else {
      setSelectedImage(null);
      setBaseImageUrl(null);
      setProcessedImageUrl(null);
    }
  }, [article]);

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || '',
        image_url: article.image_url || ''
      });
    }
  }, [article]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load user settings
  useEffect(() => {
    if (user?.id) {
      loadUserSettings();
    }
  }, [user?.id]);

  const loadUserSettings = async () => {
    if (!user?.id) return;
    
    try {
      const settings = await getUserSettings(user.id);
      setUserSettings(settings);
    } catch (error) {
      console.error('Failed to load user settings for stories:', error);
      // Settings will remain null and functions will use defaults
    }
  };

  const generatePreview = useCallback(async (title: string, imageUrl: string) => {
    if (!title.trim()) {
      // If title is empty, show base image without text
      setSelectedImage(imageUrl);
      setProcessedImageUrl(null);
      setFormData(prev => ({
        ...prev,
        image_url: imageUrl
      }));
      return;
    }

    setIsGeneratingPreview(true);
    try {
      const imageWithText = await createImageWithTextPreview(imageUrl, title, userSettings || undefined);
      setSelectedImage(imageWithText);
      setProcessedImageUrl(imageWithText);
      setFormData(prev => ({
        ...prev,
        image_url: imageWithText
      }));
    } catch (error) {
      console.error('Error generating preview:', error);
      // Keep the base image if preview fails
      setSelectedImage(imageUrl);
      setProcessedImageUrl(null);
    } finally {
      setIsGeneratingPreview(false);
    }
  }, [userSettings]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Generate live preview for title changes if there's a base image
    if (name === 'title' && baseImageUrl) {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set new timer for debounced update (500ms delay)
      debounceTimerRef.current = setTimeout(() => {
        generatePreview(value, baseImageUrl);
      }, 500);
    }
  };

  const handleImageSelect = async (url: string) => {
    if (!url) return;

    // Store the original base image
    setBaseImageUrl(url);
    
    // If there's already a title, apply text overlay immediately
    if (formData.title.trim()) {
      setIsGeneratingPreview(true);
      try {
        const imageWithText = await createImageWithTextPreview(url, formData.title, userSettings || undefined);
        setSelectedImage(imageWithText);
        setProcessedImageUrl(imageWithText);
        setFormData(prev => ({
          ...prev,
          image_url: imageWithText
        }));
      } catch (error) {
        console.error('Error generating preview:', error);
        setSelectedImage(url);
        setProcessedImageUrl(null);
        setFormData(prev => ({
          ...prev,
          image_url: url
        }));
      } finally {
        setIsGeneratingPreview(false);
      }
    } else {
      // No title yet, just show the base image
      setSelectedImage(url);
      setProcessedImageUrl(null);
      setFormData(prev => ({
        ...prev,
        image_url: url
      }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!article?.id) return;

    setIsDeleting(true);
    try {
      const response = await deleteStory(article.id);
      if (response?.status === 'success') {
        setToastMessage('Historia eliminada correctamente');
        setToastType('success');
        setShowToast(true);
        setReload((prev) => !prev);
      } else {
        setToastMessage('Error al eliminar la historia');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      setToastMessage('Error al eliminar la historia');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsDeleting(false);
      setShowModalConfirmDelete(false);
      setArticle(null);
    }
  };

  const handleConfirmEdit = async () => {
    setIsSaving(true);
    
    // Generate final standardized image for saving if we have a base image and title
    let finalImageUrl = formData.image_url;
    
    if (baseImageUrl && formData.title.trim()) {
      try {
        // Use the preview function to preserve original dimensions for final save
        const processedImageDataUrl = await createImageWithTextPreview(baseImageUrl, formData.title, userSettings || undefined);
        
        // Upload processed image but DON'T add to images table (so ImagePicker stays clean)
        if (processedImageDataUrl.startsWith('data:')) {
          console.log('🔄 Converting data URL to file and uploading...');
          const filename = `story-processed-${Date.now()}.jpg`;
          const file = dataUrlToFile(processedImageDataUrl, filename);
          
          // Upload the processed image file with a special type that won't show in ImagePicker
          const uploadResponse = await uploadImage(file, { type: 'processed' });
          if (uploadResponse && uploadResponse.image_url) {
            finalImageUrl = uploadResponse.image_url;
            console.log('✅ Processed image uploaded successfully:', finalImageUrl);
          } else {
            throw new Error('Failed to upload processed image');
          }
        } else {
          finalImageUrl = processedImageDataUrl;
        }
      } catch (error) {
        console.error('Error generating/uploading final image:', error);
        // Fall back to current image if processing/upload fails
      }
    }

    const finalFormData: NewStory = {
      title: formData.title,
      image_url: finalImageUrl,
      base_image_url: baseImageUrl || undefined
    };

    // Log the image size for debugging
    if (finalImageUrl) {
      const sizeInBytes = finalImageUrl.length;
      const sizeInKB = (sizeInBytes / 1024).toFixed(2);
      const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
      console.log('📊 Final image payload size:', {
        bytes: sizeInBytes,
        KB: sizeInKB,
        MB: sizeInMB,
        isDataUrl: finalImageUrl.startsWith('data:')
      });
    }

    if (creationType === 'create') {
      try {
        const createdStory = await createStory(finalFormData);
        if (createdStory) {
          setReload((prev) => !prev);
          setToastMessage('Historia creada correctamente');
          setToastType('success');
          setShowToast(true);
          setShowModalEdit(false);
          setArticle(null);
        } else {
          setToastMessage('Error al crear la historia');
          setToastType('error');
          setShowToast(true);
        }
      } catch (error) {
        console.error('Error creating story:', error);
        setToastMessage('Error al crear la historia');
        setToastType('error');
        setShowToast(true);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Update story
    if (!article?.id) {
      setIsSaving(false);
      return;
    }

    const updatedStory: Story = {
      id: article.id,
      title: finalFormData.title,
      image_url: finalFormData.image_url,
      base_image_url: finalFormData.base_image_url || undefined,
      published: article.published,
      isPublished: article.isPublished
    };

    try {
      const result = await updateStory(updatedStory);
      if (result) {
        setReload((prev) => !prev);
        setToastMessage('Historia actualizada correctamente');
        setToastType('success');
        setShowToast(true);
        setShowModalEdit(false);
        setArticle(null);
      } else {
        setToastMessage('Error al actualizar la historia');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error updating story:', error);
      setToastMessage('Error al actualizar la historia');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsImagePickerOpen(false);
  };

  function handleShowArticle(
    article: Article | Story,
    setShowArticleModal: React.Dispatch<React.SetStateAction<boolean>>,
    setArticle: React.Dispatch<React.SetStateAction<Article | Story | null>>): void {
    setArticle(article);
    setShowArticleModal(true);
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleAIGenerate = (generatedContent: { title: string }) => {
    setFormData({
      title: generatedContent.title,
      image_url: ''
    });
    setCreationType('create');
    setShowModalEdit(true);
  };

  const handleIconAiClick = () => {
    if (isSaving) {
      return;
    }
    setShowAIModal(true);
  };

  const handlePartialGenerate = (partialContent: { title: string }) => {
    setFormData(prev => ({
      ...prev,
      title: partialContent.title
    }));
  };

  const generateAIImage = () => {
    if (isSaving) {
      return;
    }
    if (!formData.title) {
      setToastMessage('No se puede generar una imagen sin título');
      setToastType('error');
      setShowToast(true);
      return;
    }
    setShowAIImageConfirmModal(true);
  };

  const handleConfirmAIImageGeneration = () => {
    setShowAIImageConfirmModal(false);
    setIsGeneratingImage(true);
    setPreviousImage(selectedImage);
    setSelectedImage(null);
    console.log('generateAIImage');
    generateImage(formData.title)
      .then(async (response) => {
        console.log('🎯 AI Image generation response:', response);
        if (response && 'fileUrl' in response) {
          console.log('🔥 STARTING TEXT OVERLAY PROCESS');
          console.log('📸 Original image URL:', response.fileUrl);
          console.log('📝 Story title:', formData.title);
          
          try {
            // Apply text overlay to the generated image - preserve original dimensions
            console.log('🚀 Calling createImageWithTextPreview...');
            const imageWithTextDataUrl = await createImageWithTextPreview(response.fileUrl, formData.title, userSettings || undefined);
            console.log('✅ Text overlay completed, result length:', imageWithTextDataUrl.length);
            
            // Upload the processed image instead of storing data URL
            if (imageWithTextDataUrl.startsWith('data:')) {
              console.log('🔄 Uploading processed AI image...');
              const filename = `ai-story-${Date.now()}.jpg`;
              const file = dataUrlToFile(imageWithTextDataUrl, filename);
              
              const uploadResponse = await uploadImage(file, { type: 'processed' });
              if (uploadResponse && uploadResponse.image_url) {
                console.log('✅ Processed AI image uploaded successfully:', uploadResponse.image_url);
                setBaseImageUrl(response.fileUrl);
                setSelectedImage(uploadResponse.image_url);
                setProcessedImageUrl(uploadResponse.image_url);
                setFormData(prev => ({
                  ...prev,
                  image_url: uploadResponse.image_url
                }));
              } else {
                throw new Error('Failed to upload processed AI image');
              }
            } else {
              setBaseImageUrl(response.fileUrl);
              setSelectedImage(imageWithTextDataUrl);
              setProcessedImageUrl(imageWithTextDataUrl);
              setFormData(prev => ({
                ...prev,
                image_url: imageWithTextDataUrl
              }));
            }
            console.log('💾 Updated state with uploaded image URL');
          } catch (overlayError) {
            console.error('❌ Error applying text overlay:', overlayError);
            console.error('❌ Full error details:', {
              name: overlayError instanceof Error ? overlayError.name : 'Unknown',
              message: overlayError instanceof Error ? overlayError.message : String(overlayError),
              stack: overlayError instanceof Error ? overlayError.stack : 'No stack trace'
            });
            // Fallback to original image if text overlay fails
            setBaseImageUrl(response.fileUrl);
            setSelectedImage(response.fileUrl);
            setFormData(prev => ({
              ...prev,
              image_url: response.fileUrl
            }));
            console.log('⚠️ Fell back to original image');
          }
        } else {
          console.log('❌ No valid response from image generation');
        }
      })
      .catch((error) => {
        console.error('Error generating image:', error);
        setToastMessage('Error al generar la imagen. Por favor, inténtelo de nuevo.');
        setToastType('error');
        setShowToast(true);
        if (previousImage) {
          setSelectedImage(previousImage);
          setFormData(prev => ({
            ...prev,
            image_url: previousImage
          }));
        } else if (article?.image_url) {
          setSelectedImage(article.image_url);
          setFormData(prev => ({
            ...prev,
            image_url: article.image_url
          }));
        }
      })
      .finally(() => {
        setIsGeneratingImage(false);
      });
  };

  return (
    <div>
      <h2 className='page-title'>Historias</h2>
      <DataTable
        columnDefinitions={columnDefinitions}
        filters={filters}
        refresh={reload}
        onShowArticle={(article) => handleShowArticle(article, setShowArticleModal, setArticle)}
        dataSource='stories'
      />

      <div className='create-article-button-container'>
        <button
          className='button-primary'
          onClick={() => {
            setArticle(null);
            setFormData({ title: '', image_url: '' });
            setBaseImageUrl(null);
            setSelectedImage(null);
            setCreationType('create');
            setShowModalEdit(true);
          }}
        >
          Crear historia
        </button>
      </div>

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      <ConfirmModal
        show={showModalConfirmDelete}
        title="Borrar historia"
        text={`¿Confirma la eliminación de "${article?.title || ''}"?`}
        onClose={() => {
          if (!isDeleting) {
            setShowModalConfirmDelete(false);
            setArticle(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      <EditModal
        type='story'
        show={showModalEdit}
        title={creationType === 'create' ? 'Agregar historia' : 'Editar historia'}
        onClose={() => {
          if (!isSaving) {
            setShowModalEdit(false);
            setArticle(null);
          }
        }}
        onConfirm={handleConfirmEdit}
        onGenerate={handleAIGenerate}
        onPartialGenerate={handlePartialGenerate}
        isGenerating={isGeneratingImage || isSaving}
      >
        <form onSubmit={handleSubmit}>
          <div>
            <label>Título</label>
            <img
              src={icon_ai}
              height="30"
              width="30"
              alt="icon_ai"
              style={{ 
                marginLeft: '10px', 
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.5 : 1
              }}
              onClick={handleIconAiClick}
            />
            <br />
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label>Imagen</label>
            <img
              src={icon_ai}
              height="30"
              width="30"
              alt="icon_ai"
              style={{ 
                marginLeft: '10px', 
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.5 : 1
              }}
              onClick={generateAIImage}
            />
            <br />
            <div>
              {selectedImage ? (
                <img
                  className='edit-form-image'
                  src={selectedImage}
                  alt="Selected"
                />
              ) : isGeneratingImage ? (
                <div className="image-loading">
                  <div className="dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="loading-text">Generando imagen...</div>
                </div>
              ) : isGeneratingPreview ? (
                <div className="image-loading">
                  <div className="dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="loading-text">Actualizando vista previa...</div>
                </div>
              ) : (
                <div 
                  className="image-placeholder clickable-placeholder"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsImagePickerOpen(true);
                  }}
                >
                  Ninguna imagen seleccionada
                </div>
              )}
              <br />
              <button
                type="button"
                className="button-primary"
                onClick={(e) => {
                  e.preventDefault();
                  setIsImagePickerOpen(true);
                }}
                disabled={isGeneratingImage || isSaving}
              >
{isGeneratingImage ? "Generando..." : isSaving ? "Guardando..." : "Cambiar imagen"}
              </button>
              <ImagePicker
                isOpen={isImagePickerOpen}
                onClose={handleClose}
                onSelect={handleImageSelect}
              />
            </div>
          </div>
        </form>
      </EditModal>

      <Modal
        isOpen={showArticleModal}
        onClose={() => {
          setShowArticleModal(false);
          setArticle(null);
        }}
        title='Ver historia'
      >
        <div>
          <img src={article?.image_url} alt="Story Image" className="modal-image" />
        </div>
      </Modal>

      <AIGenerateModal
        type='story'
        show={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerate}
        onPartialGenerate={handlePartialGenerate}
      />

      <ConfirmModal
        onClose={() => setShowAIImageConfirmModal(false)}
        onConfirm={handleConfirmAIImageGeneration}
        show={showAIImageConfirmModal}
        title="Generar imagen con IA"
        text="Esta acción costará alrededor de 0.05 USD"
        confirmButtonColor="blue"
      />
    </div>
  )
}
